import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  useTable,
  useSortBy,
  useExpanded,
  usePagination,
  useFilters,
  useGlobalFilter,
  useAsyncDebounce
} from "react-table";
import classNames from "classnames";
import { Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faSearch,
  faTh,
  faThList,
  faSort,
  faSortDown,
  faSortUp
} from "@fortawesome/free-solid-svg-icons";

export const GlobalFilter = ({ globalFilter, setGlobalFilter }) => {
  const [value, setValue] = useState(globalFilter);
  const onChange = useAsyncDebounce(val => {
    setGlobalFilter(val || undefined);
  }, 200);

  return (
    <div className="datatable__search">
      <FontAwesomeIcon icon={faSearch} />
      <input
        value={value || ""}
        onChange={e => {
          setValue(e.target.value);
          onChange(e.target.value);
        }}
        placeholder="Search"
      />
    </div>
  );
};

GlobalFilter.propTypes = {
  globalFilter: PropTypes.string,
  setGlobalFilter: PropTypes.func.isRequired
};

GlobalFilter.defaultProps = {
  globalFilter: ""
};

const DataTable = ({
  columns,
  data,
  options = {},
  setView,
  showGlobalSearch,
  onLastPage,
  displayTimeFormatMessage
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    visibleColumns,
    rows,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    preGlobalFilteredRows,
    setGlobalFilter,
    setAllFilters,
    state: { pageIndex, pageSize, globalFilter }
  } = useTable(
    {
      columns,
      data,
      initialState: {
        ...{
          pageSize: 10
        },
        ...options
      }
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination
  );
  const pageMin = pageIndex * pageSize + 1;
  const pageMax = pageIndex * pageSize + pageSize;
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (options.filters) {
      setAllFilters(options.filters);
    }
  }, [columns, data, options, setAllFilters]);

  const handleSelected = e => {
    if (e.target.checked) {
      // eslint-disable-next-line no-shadow
      setSelected(selected => [...selected, { id: e.target.name }]);
    } else {
      setSelected(selected.filter(s => s.id !== e.target.name));
    }
  };

  const handleSelectAll = e => {
    if (e.target.checked) {
      const newSelected = [];
      rows.map(row => newSelected.push({ id: row.values.id }));
      setSelected(newSelected);
    } else {
      setSelected([]);
    }
  };
  useEffect(() => {
    if (!canNextPage && onLastPage && pageIndex !== 0) {
      onLastPage({ pageIndex, gotoPage });
    }
  }, [canNextPage]);

  useEffect(() => {
    if (options.checkboxes) {
      options.onCheckboxChange(selected);
    }
  }, [options, selected]);

  return (
    <div className="datatable">
      {showGlobalSearch && (
        <div className="datatable__header">
          <div className="datatable__header-group">
            <GlobalFilter
              preGlobalFilteredRows={preGlobalFilteredRows}
              globalFilter={globalFilter}
              setGlobalFilter={setGlobalFilter}
            />
            {setView && (
              <div className="view__actions">
                <button
                  type="button"
                  className="btn btn-sm btn-default"
                  onClick={() => setView("cards")}
                >
                  <FontAwesomeIcon icon={faTh} size="lg" />
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-default"
                  disabled
                >
                  <FontAwesomeIcon icon={faThList} size="lg" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {rows.length === 0 && <div className="datatable__empty">No results</div>}
      {rows.length > 0 && (
        <>
          {!displayTimeFormatMessage && (
            <div
              style={{ display: "flex", justifyContent: "end", fontSize: 11 }}
            >
              <i>Time values displayed as US Eastern (UTC -04:00/-05.00)</i>
            </div>
          )}
          <div className="datatable__table">
            <Table {...getTableProps()}>
              <thead>
                {headerGroups.map(headerGroup => (
                  <tr
                    {...headerGroup.getHeaderGroupProps()}
                    key={`head-${headerGroup.id}`}
                  >
                    {options.checkboxes && (
                      <th className="text-center">
                        <input
                          onChange={handleSelectAll}
                          type="checkbox"
                          checked={selected.length === rows.length}
                        />
                      </th>
                    )}
                    {headerGroup.headers.map(column => (
                      <th
                        {...column.getHeaderProps([
                          {
                            className: `col-${column.id}`
                          },
                          column.getSortByToggleProps()
                        ])}
                      >
                        {column.render("Header")}
                        {column.canSort && (
                          <FontAwesomeIcon
                            className="ml-1"
                            icon={
                              // eslint-disable-next-line no-nested-ternary
                              column.isSorted
                                ? column.isSortedDesc
                                  ? faSortDown
                                  : faSortUp
                                : faSort
                            }
                          />
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.map((row, i) => {
                  prepareRow(row);
                  const isChecked = selected.some(s => s.id === row.values.id);
                  return (
                    <React.Fragment key={`row-${row.values.id}`}>
                      <tr
                        {...row.getRowProps()}
                        key={`row-${row.id}`}
                        className={classNames({
                          "row-expanded": row.isExpanded,
                          "row-alt": i % 2 !== 1
                        })}
                      >
                        {options.checkboxes && (
                          <td className="text-center">
                            <input
                              onChange={handleSelected}
                              name={row.values.id}
                              type="checkbox"
                              checked={isChecked}
                            />
                          </td>
                        )}
                        {row.cells.map(cell => {
                          return (
                            <td
                              {...cell.getCellProps({
                                className: `col-${cell.column.id}`
                              })}
                            >
                              <div>
                                {cell.render("Cell", {
                                  row,
                                  value: cell.value
                                })}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                      {row.isExpanded ? (
                        <tr
                          {...row.getRowProps()}
                          key={`row-${row.id}-x`}
                          className={classNames("row-expand", {
                            "row-alt": i % 2 !== 1
                          })}
                        >
                          <td
                            colSpan={visibleColumns.length}
                            className="col-expand"
                          >
                            {row.values.expand}
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          </div>
          <div className="datatable__pagination">
            <div className="datatable__pagination-page">
              <span>Page</span>
              <input
                type="number"
                value={pageIndex + 1}
                onChange={e => {
                  const pg = e.target.value ? Number(e.target.value) - 1 : 0;
                  gotoPage(pg);
                }}
                onFocus={e => {
                  e.target.select();
                }}
                className="form-control"
                min="1"
                max={pageOptions.length}
                disabled={pageOptions.length === 1}
              />
              <span>of {pageOptions.length}</span>
            </div>
            <div className="datatable__pagination-pagesize">
              <select
                value={pageSize}
                onChange={e => {
                  setPageSize(Number(e.target.value));
                }}
                className="form-control"
              >
                {[10, 20, 30].map(ps => (
                  <option key={ps} value={ps}>
                    Show {ps}
                  </option>
                ))}
              </select>
            </div>
            <div
              className="datatable__pagination-buttons"
              role="group"
              aria-label="pagination"
            >
              <div className="btn-group">
                <button
                  type="button"
                  onClick={() => previousPage()}
                  className="btn btn-default"
                  disabled={!canPreviousPage}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <button
                  type="button"
                  onClick={() => nextPage()}
                  className="btn btn-default"
                  disabled={!canNextPage}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

DataTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  options: PropTypes.object.isRequired,
  setView: PropTypes.func
};

DataTable.defaultProps = {
  setView: null
};

export default DataTable;
